import { devMode, getHost } from "@src/env";
import type {
  HistoryTrackInfo,
  RequestableTrackInfo,
  TrackInfo,
} from "@src/types";

import { Api } from "@src/services/api";

import { getSlug } from "@src/utils/getSlug";

import type {
  MercureTrack,
  NextTrackResponse,
  RequestTrackResponse,
  RequestableTracksResponse,
  StatusResponse,
  StreamInfo,
} from "./types";

export class RadioCo {
  // #host = "https://public.radio.co";
  #host = devMode ? "https://public.radio.co" : `${getHost()}/radioco`;

  #streaming_id: string;

  #eventSource: EventSource | null = null;

  #currentTrack: MercureTrack | null = null;

  #trackHistory: MercureTrack[] = [];

  #status: StatusResponse["data"] | null = null;

  /**
   * @param streaming_id Example: `sf38694d4a`
   */
  constructor(streaming_id: string) {
    this.#streaming_id = streaming_id;
  }

  getTrackInfo(
    artistTitle: string,
    artwork_small: string,
    artwork_large: string,
  ): TrackInfo {
    const split = artistTitle.split(" - ");
    const [artist, ...title] = split;
    return {
      artist,
      title: title.join(" - "),
      artwork_small,
      artwork_large,
    };
  }

  async #getCurrentTrack(data: MercureTrack): Promise<TrackInfo> {
    if (data) {
      return {
        ...this.getTrackInfo(
          data.title,
          data.artwork_urls.standard,
          data.artwork_urls.large,
        ),
        ...(data.album ?? data.track_album ? { album: data.album ?? data.track_album } : {}),
        ...(data.playout_duration ?? data.track_playout_duration ? { duration: data.playout_duration ?? data.track_playout_duration } : {}),
      };
    }

    console.warn(
      "Não foi possível determinar a faixa atual da rádio. Obtendo pelas últimas tocadas...",
    );
    const trackHistory = await this.getTrackHistory();
    if (trackHistory.length > 0) {
      const historyTrack = trackHistory[0];
      return {
        title: historyTrack.title,
        artist: historyTrack.artist,
        artwork_small: historyTrack.artwork_small,
        artwork_large: historyTrack.artwork_large,
      };
    }

    console.warn(
      "Não foi possível obter as últimas tocadas da rádio. Assumindo que não há faixa atual...",
    );
    return {
      title: "",
      artist: "",
      artwork_small: "/offline.png",
      artwork_large: "/offline.png",
    };
  }

  async getCurrentTrack(): Promise<TrackInfo> {
    return new Promise((resolve, reject) => {
      if (this.#currentTrack) {
        // console.log("[getCurrentTrack] obtido com Mercure");
        this.#getCurrentTrack(this.#currentTrack).then(resolve);
      } else {
        // console.log("[getCurrentTrack] obtido sem Mercure");
        const url = `${this.#host}/api/v2/${this.#streaming_id}/track/current`;
        fetch(url)
          .then((response) => response.json())
          .then((response) => {
            const data: MercureTrack = response.data;
            this.#currentTrack = data;
            this.#getCurrentTrack(this.#currentTrack).then(resolve);
          })
          .catch((error) => {
            console.warn("[getCurrentTrack] error:", error);
            reject(error);
          });
      }
    });
  }

  async getNextTrack(): Promise<TrackInfo | null> {
    return new Promise((resolve) => {
      const url = `${this.#host}/stations/${this.#streaming_id}/next`;
      fetch(url)
        .then((response) => response.json())
        .then((response: NextTrackResponse) => {
          if (!response.next_track) {
            resolve(null);
            return;
          }
          const trackInfo = this.getTrackInfo(
            response.next_track.title,
            response.next_track.artwork_url,
            response.next_track.artwork_url_large,
          );
          resolve(trackInfo);
        })
        .catch((error) => {
          console.warn("[getNextTrack] error:", error);
          resolve(null);
        });
    });
  }

  #getTrackHistory(data: MercureTrack[]): HistoryTrackInfo[] {
    return data.map((item) => {
      const trackInfo = this.getTrackInfo(
        item.title,
        item.artwork_urls.standard,
        item.artwork_urls.large,
      );
      return {
        ...trackInfo,
        datetime: item.start_time,
      };
    });
  }

  async getTrackHistory(): Promise<HistoryTrackInfo[]> {
    return new Promise((resolve, reject) => {
      if (this.#trackHistory.length > 0) {
        // console.log("[getTrackHistory] obtido com Mercure");
        resolve(this.#getTrackHistory(this.#trackHistory));
      } else {
        // console.log("[getTrackHistory] obtido sem Mercure");
        const url = `${this.#host}/api/v2/${this.#streaming_id}/track/history`;
        fetch(url)
          .then((response) => response.json())
          .then((response) => {
            const data: MercureTrack[] = response.data;
            if (Array.isArray(data)) {
              this.#trackHistory = data;
              resolve(this.#getTrackHistory(this.#trackHistory));
            } else {
              reject(`Data is not an array: ${JSON.stringify(data)}`);
            }
          })
          .catch((error) => {
            console.warn("[getTrackHistory] error:", error);
            reject(error);
          });
      }
    });
  }

  #isOnAir(data: StatusResponse["data"]): boolean {
    if (!data) {
      console.warn(
        "Não foi possível determinar se a rádio está no ar. Assumindo que esteja...",
      );
      return true;
    }
    return data.status === "onair";
  }

  async isOnAir(allowMercure: boolean = true): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (allowMercure && this.#status) {
        // console.log("[isOnAir] obtido com Mercure");
        resolve(this.#isOnAir(this.#status));
      } else {
        // console.log("[isOnAir] obtido sem Mercure");
        const url = `${this.#host}/api/v2/${this.#streaming_id}/status`;
        fetch(url)
          .then((response) => response.json())
          .then((response: StatusResponse) => {
            this.#status = response.data;
            resolve(this.#isOnAir(this.#status));
          })
          .catch((error) => {
            console.warn("[isOnAir] error:", error);
            reject(error);
          });
      }
    });
  }

  async getRequestableTracks(): Promise<RequestableTrackInfo[]> {
    return new Promise((resolve, reject) => {
      const url = `${this.#host}/stations/${this.#streaming_id}/requests/tracks`;
      fetch(url, { method: "GET" })
        .then((response) => response.json())
        .then((response: RequestableTracksResponse) => {
          const tracks = response.tracks.map((track) => ({
            id: track.id,
            artist: track.artist,
            title: track.title,
            artwork_small: track.artwork.url || "/logo_white.png",
            artwork_large: track.artwork.large_url || "/logo_white.png",
            keywords: getSlug(`${track.artist} ${track.title}`),
          }));
          resolve(tracks);
        })
        .catch((error) => {
          console.warn("[getRequestableTracks] error:", error);
          reject(error);
        });
    });
  }

  async requestTrack(track_id: number): Promise<boolean> {
    return new Promise((resolve) => {
      const url = `${this.#host}/stations/${this.#streaming_id}/requests`;
      fetch(url, {
        method: "POST",
        body: JSON.stringify({ track_id }),
        headers: {
          "Content-Type": "application/json",
          // The device fingerprint ID to associate where a request came from
          // 'X-Device': '',
        },
      })
        .then((response) => response.json())
        .then((response: RequestTrackResponse) => {
          const errors = response?.errors || [];
          resolve(errors.length === 0);
        })
        .catch((error) => {
          console.warn("[requestTrack] error:", error);
          resolve(false);
        });
    });
  }

  static async getStreamInfo(url: string): Promise<StreamInfo> {
    return new Promise((resolve, reject) => {
      Api.getHeaders(url)
        .then((response) => {
          const responseObj = response || {};

          let audioFormat = "";
          if ("Content-Type" in responseObj) {
            audioFormat = responseObj["Content-Type"].split("/")[1];
          }

          let channels = 0;
          let samplerate = 0;
          let bitrate = 0;
          if ("ice-audio-info" in responseObj) {
            const regex = /channels=(\d+);samplerate=(\d+);bitrate=(\d+)/;
            const match = responseObj["ice-audio-info"].match(regex);
            if (match) {
              channels = parseInt(match[1]);
              samplerate = parseInt(match[2]);
              bitrate = parseInt(match[3]);
            }
          }

          let icyName = "";
          if ("icy-name" in responseObj) {
            icyName = responseObj["icy-name"];
          }

          let server = "";
          if ("Server" in responseObj) {
            server = responseObj["Server"];
          }

          const streamInfo: StreamInfo = {
            name: icyName,
            data_source: server,
            audio_format: audioFormat as StreamInfo["audio_format"],
            channels,
            samplerate,
            bitrate,
          };
          resolve(streamInfo);
        })
        .catch(reject);
    });
  }

  startEventSource(raspberry: boolean) {
    if (this.#eventSource) return;

    // https://developers-84608658bd058c817.radio.co/api-reference/openapi_specs/public-v2
    const endpoint = "https://mercure.radio.co/.well-known/mercure";
    const topics = [
      `https://public.radio.co/api/v2/${this.#streaming_id}/status`,
    ];
    if (!raspberry) {
      topics.push(
        `https://public.radio.co/api/v2/${this.#streaming_id}/track/current`,
      );
      topics.push(
        `https://public.radio.co/api/v2/${this.#streaming_id}/track/history`,
      );
      // topics.push(
      //   `https://public.radio.co/api/v2/${this.#streaming_id}/track/next`,
      // );
    }
    const params = new URLSearchParams();
    topics.forEach((topic) => params.append("topic", topic));
    params.append("Last-Event-ID", "null");
    const url = `${endpoint}?${params.toString()}`;

    this.#eventSource = new EventSource(url);
    this.#eventSource.addEventListener("open", () => {
      console.log("[EventSource] connection opened.");
    });
    this.#eventSource.addEventListener(
      `${this.#streaming_id}/status`,
      (event) => {
        if (!event.data) return;
        // console.log("[EventSource] status event received.", event.data);
        try {
          const data: StatusResponse["data"] = JSON.parse(event.data);
          this.#status = data;
        } catch {
          console.warn(
            "[EventSource] status event data is not valid JSON:",
            event.data,
          );
        }
      },
    );
    this.#eventSource.addEventListener(
      `${this.#streaming_id}/track/current`,
      (event) => {
        if (!event.data) return;
        // console.log("[EventSource] current track event received.", event.data);
        try {
          const data: MercureTrack = JSON.parse(event.data);
          this.#currentTrack = data;
        } catch {
          console.warn(
            "[EventSource] current track event data is not valid JSON:",
            event.data,
          );
        }
      },
    );
    this.#eventSource.addEventListener(
      `${this.#streaming_id}/track/history`,
      (event) => {
        if (!event.data) return;
        // console.log("[EventSource] history track event received.", event.data);
        try {
          const data: MercureTrack = JSON.parse(event.data);
          this.#trackHistory.unshift(data);
          if (this.#trackHistory.length > 10) {
            this.#trackHistory.pop();
          }
        } catch {
          console.warn(
            "[EventSource] history track event data is not valid JSON:",
            event.data,
          );
        }
      },
    );
    // this.#eventSource.addEventListener(
    //   `${this.#streaming_id}/track/next`,
    //   (event) => {
    //     if (!event.data) return;
    //     console.log("[EventSource] next track event received.", event.data);
    //   },
    // );
    this.#eventSource.addEventListener("error", () => {
      console.log("[EventSource] connection lost.");
      this.stopEventSource();
      // Mercure caiu: descarta os caches para os getters voltarem ao HTTP até a
      // conexão retornar e repopular — evita servir faixa/estado congelados.
      this.invalidateCache();
    });
  }

  stopEventSource() {
    if (this.#eventSource) {
      this.#eventSource.close();
      this.#eventSource = null;
      console.log("[EventSource] connection closed.");
    }
  }

  /**
   * Descarta os caches populados pelo Mercure, forçando o próximo
   * `getCurrentTrack`/`getTrackHistory`/`isOnAir` a buscar via HTTP. Usado quando
   * o dado do Mercure está velho — ex.: tempo decorrido > duração da faixa
   * (CurrentTrackProgress) ou queda da conexão (handler de `error` acima). Sem
   * isso, esses getters relêem o cache do Mercure para sempre e o refetch da
   * query `tracks` vira no-op.
   */
  invalidateCache() {
    this.#currentTrack = null;
    this.#trackHistory = [];
    this.#status = null;
  }
}
